/**
 * HeroStrip — iter86.95 One UI Phase 1. The collapsing hero strip.
 *
 * Replaces BOTH the retired DashboardHeader and the tournament hero
 * card: one sticky glass row (~56px) with home mark, Rivalry label +
 * tournament name, canonical status pill, mentions chip, switcher and
 * the menu trigger. The pills row (activity / teams / template /
 * window / draft CTA) folds into an expandable detail panel.
 * ONE component tree for mobile and desktop — CSS handles the rest.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AtSign, Check, ChevronDown, Users, Zap, Flame, CalendarRange, Trophy } from "lucide-react";
import TournamentSwitcher from "@/components/dashboard/TournamentSwitcher";
import DashboardMenu from "@/components/dashboard/DashboardMenu";
import { lifecyclePill } from "@/lib/lifecycle";
import { isBoostOnlyTemplate } from "@/lib/boostOnlyTemplates";
import { isDraftEligible } from "@/lib/draftEligibleTemplates";

// iter63r — Friendlier display labels for the tournament-template pill.
const TEMPLATE_LABELS = {
    world_cup_api: "World Cup Format",
    world_cup_ko_api: "World Cup KO Format",
    premier_league_api: "Premier League Season",
    la_liga_api: "La Liga Season",
    champions_league_api: "Champions League",
    club_season_api: "Club Season",
    golf_pickem_lowest_avg_api: "Golf — Lowest Average",
    golf_position_draft_api: "Golf — Position Draft",
    world_cup: "World Cup",
    world_cup_knockout: "World Cup KO",
    march_madness: "March Madness",
    football_playoffs: "Football Playoffs",
    olympics: "Olympics",
    custom: "Custom",
};

const templateLabel = (key) => {
    if (!key) return "";
    return (TEMPLATE_LABELS[key] || key.replace(/_/g, " ")).toUpperCase();
};

export default function HeroStrip({
    league,
    leagueId,
    tournament,
    tournamentCount,
    me,
    myAvatar,
    myFlair,
    isSpectator,
    seasonStanding,
    unreadMentions,
    tenantLifecycleState,
    draft,
    participants,
    liveDraftEnabled,
    setViewTournamentId,
    resetLoadThrottle,
    onNewTournament,
    setTournamentCount,
    switcherRefreshKey,
    onOpenUserGuide,
    onOpenTinyCrew,
    onOpenCrewIdentity,
    onOpenAccount,
    onOpenScoring,
    onMentionChipClick,
    onCopyInvite,
    onLogout,
    onOpenSetupWizard,
    copied,
}) {
    const [scrolled, setScrolled] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const lc = tournament ? lifecyclePill(tournament.lifecycle_state) : null;
    const hasOrphanAssignments =
        !draft && (participants || []).some((p) => (p.assigned_team_ids || []).length > 0);
    const isPotFormat =
        tournament?.golf?.mode === "pickem_lowest_avg" || tournament?.pickem?.mode === "pot";
    // iter87.465.22 (user-directed — "default templates are not
    // draft... fix correctly, no bedside again") — replaced the
    // per-template blacklist (which still missed legacy `world_cup`
    // and would keep missing future templates) with the shared
    // `isDraftEligible` whitelist helper, same gate as AdminPanel's
    // Draft tab and the dashboard StartDraftBanner.
    const isDraftless = !isDraftEligible(tournament);
    // iter87.55 → iter87.111 — Boost-only templates (CFB Weekly + NFL
    // Weekly) skip the entire draft CTA cluster — no draft, no orphan
    // assignments, no "Start Draft" link.
    const isBoostOnly = isBoostOnlyTemplate(tournament?.template);

    return (
        <header
            data-testid="hero-strip"
            className={`glass sticky top-0 z-30 border-b border-white/10 px-3 sm:px-4 lg:px-8 transition-[padding] duration-200 ${scrolled && !expanded ? "py-1.5" : "py-2.5"}`}
        >
            <div className="flex items-center gap-2.5 max-w-7xl mx-auto">
                <Link
                    to="/"
                    data-testid="home-link"
                    title="Back to home"
                    className="shrink-0 w-9 h-9 rounded-xl bg-cyber text-black flex items-center justify-center active:scale-95 transition-transform"
                >
                    <Trophy className="w-[18px] h-[18px]" strokeWidth={2.25} />
                </Link>
                <div className="min-w-0 flex-1">
                    <p
                        data-testid="league-name-header"
                        className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 truncate"
                        title={!isSpectator && league.invite_code ? `Invite code: ${league.invite_code} — share to add crewmates.` : undefined}
                    >
                        {league.name}
                    </p>
                    <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                        <h1 className="font-display font-black text-sm sm:text-base leading-tight truncate">
                            {tournament ? tournament.name : league.name}
                        </h1>
                        {/* iter86.88 — Canonical status pill (backend lifecycle_state). */}
                        {lc && (
                            <span
                                className={`pill ${lc.classes} shrink-0 !px-2 !py-0.5 !text-[9px]`}
                                data-testid="tournament-status-pill"
                            >
                                {lc.live && <span className="live-dot" />}
                                {lc.done && <Check className="w-3 h-3" />}
                                {lc.label}
                            </span>
                        )}
                        {tenantLifecycleState === "suspended" && (
                            <span
                                data-testid="tenant-lifecycle-pill-suspended"
                                title="This Rivalry's tenant is suspended"
                                className="pill shrink-0 !px-2 !py-0.5 !text-[9px] font-bold uppercase text-amber-400 border-amber-400"
                            >
                                Suspended
                            </span>
                        )}
                        {tenantLifecycleState === "archived" && (
                            <span
                                data-testid="tenant-lifecycle-pill-archived"
                                title="This Rivalry's tenant is archived"
                                className="pill shrink-0 !px-2 !py-0.5 !text-[9px] font-bold uppercase text-zinc-500 border-zinc-500"
                            >
                                Archived
                            </span>
                        )}
                    </div>
                </div>

                {/* Right cluster — mentions, switcher, expand, menu. */}
                {!isSpectator && me && unreadMentions > 0 && (
                    <button
                        type="button"
                        onClick={onMentionChipClick}
                        data-testid="unread-mentions-chip"
                        title={`${unreadMentions} unread mention${unreadMentions === 1 ? "" : "s"} in Smack Stream`}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-heat bg-heat/15 text-heat font-display font-extrabold text-[11px] px-2.5 min-h-[36px] uppercase tracking-tight active:scale-95 transition-transform"
                    >
                        <AtSign className="w-3.5 h-3.5" />
                        <span data-testid="unread-mentions-count">{unreadMentions}</span>
                    </button>
                )}
                <div className="flex shrink min-w-0">
                    <TournamentSwitcher
                        leagueId={leagueId}
                        currentTid={tournament?.id}
                        isAdmin={!!me?.is_admin}
                        onChange={(tid) => {
                            setViewTournamentId(tid);
                            resetLoadThrottle();
                        }}
                        onNewTournament={onNewTournament}
                        onCountChange={setTournamentCount}
                        refreshKey={switcherRefreshKey}
                    />
                </div>
                {tournament && (
                    <button
                        type="button"
                        data-testid="hero-expand-btn"
                        aria-expanded={expanded}
                        onClick={() => setExpanded((e) => !e)}
                        title="Tournament details"
                        className="shrink-0 w-9 h-9 rounded-full border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] flex items-center justify-center transition-colors active:scale-95"
                    >
                        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </button>
                )}
                <DashboardMenu
                    leagueId={leagueId}
                    league={league}
                    tournament={tournament}
                    tournamentCount={tournamentCount}
                    me={me}
                    myAvatar={myAvatar}
                    myFlair={myFlair}
                    isSpectator={isSpectator}
                    seasonStanding={seasonStanding}
                    onOpenUserGuide={onOpenUserGuide}
                    onOpenTinyCrew={onOpenTinyCrew}
                    onOpenCrewIdentity={onOpenCrewIdentity}
                    onOpenAccount={onOpenAccount}
                    onOpenScoring={onOpenScoring}
                    onCopyInvite={onCopyInvite}
                    copied={copied}
                    onLogout={onLogout}
                    onOpenSetupWizard={onOpenSetupWizard}
                />
            </div>

            {/* Expandable detail — the old hero-card pills fold in here. */}
            {expanded && tournament && (
                <div
                    className="max-w-7xl mx-auto pt-2.5 pb-1 flex flex-wrap items-center gap-2"
                    data-testid="hero-detail-panel"
                >
                    {/* iter86.88.1 — Canonical activity pill (backend-derived). */}
                    {tournament.activity_label && (
                        <span className="pill bg-cyber text-black border-black" data-testid="tournament-activity-pill">
                            <Flame className="w-3 h-3" /> {tournament.activity_label}
                        </span>
                    )}
                    <span className="pill bg-ink border-[#333] text-white">
                        <Users className="w-3 h-3" /> {(tournament.teams || []).length} {tournament.format === "golf" ? "golfers" : "teams"}
                    </span>
                    <span className="pill bg-ink border-[#333] text-white" data-testid="tournament-template-pill">
                        {templateLabel(tournament.template)}
                    </span>
                    {tournament.season_window && (
                        <span className="pill bg-ink border-volt/60 text-volt" data-testid="tournament-window-pill">
                            <CalendarRange className="w-3 h-3" />
                            {tournament.season_window === "first_half" ? "1st Half" : "2nd Half"}
                            {tournament.season_window_rounds
                                ? ` · MW ${tournament.season_window_rounds.from}–${tournament.season_window_rounds.to}`
                                : ""}
                        </span>
                    )}

                    {/* Draft status / CTA cluster (iter54 / iter86.42 logic preserved). */}
                    {!draft && me?.is_admin && isPotFormat && !isBoostOnly && (
                        <span
                            className="pill bg-cyber/15 border-cyber text-cyber"
                            data-testid="golf-pot-picks-pill"
                            title="Crewmates pick from the pots below — no draft needed"
                        >
                            {tournament?.pickem?.mode === "pot" ? "🎯" : "⛳"} Pot picks — no draft
                        </span>
                    )}
                    {!draft && !hasOrphanAssignments && me?.is_admin && !isDraftless && !isBoostOnly && (
                        <Link to={`/league/${leagueId}/draft`} className="brutal-btn-primary py-2 text-xs" data-testid="start-draft-link">
                            <Zap className="w-3.5 h-3.5" /> Start Draft
                        </Link>
                    )}
                    {!draft && hasOrphanAssignments && !isDraftless && !isBoostOnly && (
                        <span
                            className="pill bg-neon/20 border-neon text-neon"
                            data-testid="draft-recovered-pill"
                            title="Teams have been drafted (legacy data — draft pointer recovered)"
                        >
                            <Check className="w-3 h-3" /> Draft complete
                        </span>
                    )}
                    {draft && draft.status === "active" && liveDraftEnabled && (
                        <Link to={`/league/${leagueId}/draft`} className="brutal-btn-secondary py-2 text-xs" data-testid="join-draft-link">
                            <span className="live-dot" /> Live Draft
                        </Link>
                    )}
                    {draft && draft.status === "complete" && (
                        <span className="pill bg-neon/20 border-neon text-neon">
                            <Check className="w-3 h-3" /> Draft complete
                        </span>
                    )}
                </div>
            )}
        </header>
    );
}
